import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Mail, Shield, CheckCircle, XCircle, AlertCircle, Loader2,
  RefreshCw, Eye, EyeOff, ExternalLink, Save, TestTube, Info, Settings2,
  Globe, Key, Link2, Clock, Zap, Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Provider icons/logos
const GmailIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path fill="#F25022" d="M0 0h11.377v11.372H0z"/>
    <path fill="#00A4EF" d="M0 12.623h11.377V24H0z"/>
    <path fill="#7FBA00" d="M12.623 0H24v11.372H12.623z"/>
    <path fill="#FFB900" d="M12.623 12.623H24V24H12.623z"/>
  </svg>
);

export const EmailSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  
  // Settings state
  const [settings, setSettings] = useState({
    // Gmail OAuth settings
    gmail_enabled: false,
    gmail_client_id: '',
    gmail_client_secret: '',
    gmail_redirect_uri: '',
    gmail_status: 'not_configured',
    
    // Microsoft 365 OAuth settings
    microsoft_enabled: false,
    microsoft_client_id: '',
    microsoft_client_secret: '',
    microsoft_tenant_id: '',
    microsoft_redirect_uri: '',
    microsoft_status: 'not_configured',
    
    // General email settings
    sync_frequency_minutes: 5,
    max_emails_per_sync: 100,
    auto_link_contacts: true,
    auto_link_jobs: true,
    default_signature: '',
    
    // AI settings
    ai_drafts_enabled: true,
    ai_draft_review_required: true,
    ai_auto_categorize: true,
    
    // Notification settings
    notify_new_emails: true,
    notify_linked_emails: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/email`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch email settings:', error);
      // Don't show error - settings may not exist yet
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings/email`, settings);
      toast.success('Email settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (provider) => {
    setTesting(prev => ({ ...prev, [provider]: true }));
    try {
      const response = await axios.post(`${API}/settings/email/test/${provider}`);
      if (response.data.success) {
        toast.success(`${provider === 'gmail' ? 'Gmail' : 'Microsoft 365'} connection successful!`);
        setSettings(prev => ({ ...prev, [`${provider}_status`]: 'connected' }));
      } else {
        toast.error(response.data.message || 'Connection test failed');
        setSettings(prev => ({ ...prev, [`${provider}_status`]: 'error' }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
      setSettings(prev => ({ ...prev, [`${provider}_status`]: 'error' }));
    } finally {
      setTesting(prev => ({ ...prev, [provider]: false }));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Connected</Badge>;
      case 'error':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Error</Badge>;
      case 'not_configured':
        return <Badge variant="secondary"><AlertCircle className="h-3 w-3 mr-1" />Not Configured</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const toggleSecret = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getRedirectUri = (provider) => {
    const baseUrl = window.location.origin;
    return `${process.env.REACT_APP_BACKEND_URL}/api/email/oauth/${provider}/callback`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/settings')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Mail className="h-8 w-8 text-primary" />
                Email Settings
              </h1>
              <p className="text-muted-foreground">Configure email providers, OAuth credentials, and sync settings</p>
            </div>
          </div>
          <Button onClick={saveSettings} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
        </div>

        <Tabs defaultValue="providers" className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="providers" className="gap-2">
              <Globe className="h-4 w-4" />
              Providers
            </TabsTrigger>
            <TabsTrigger value="sync" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Sync
            </TabsTrigger>
            <TabsTrigger value="ai" className="gap-2">
              <Zap className="h-4 w-4" />
              AI
            </TabsTrigger>
            <TabsTrigger value="general" className="gap-2">
              <Settings2 className="h-4 w-4" />
              General
            </TabsTrigger>
          </TabsList>

          {/* Providers Tab */}
          <TabsContent value="providers" className="space-y-6">
            {/* Gmail Configuration */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <GmailIcon />
                    </div>
                    <div>
                      <CardTitle>Gmail / Google Workspace</CardTitle>
                      <CardDescription>Connect Gmail accounts using Google OAuth 2.0</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(settings.gmail_status)}
                    <Switch
                      checked={settings.gmail_enabled}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, gmail_enabled: checked }))}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-700 dark:text-blue-300">Setup Instructions:</p>
                      <ol className="list-decimal ml-4 mt-1 text-blue-600 dark:text-blue-400 space-y-1">
                        <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">Google Cloud Console <ExternalLink className="h-3 w-3" /></a></li>
                        <li>Create or select a project</li>
                        <li>Enable Gmail API in APIs & Services</li>
                        <li>Configure OAuth consent screen</li>
                        <li>Create OAuth 2.0 credentials (Web application)</li>
                        <li>Add the redirect URI shown below</li>
                      </ol>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client ID *</Label>
                    <Input
                      value={settings.gmail_client_id}
                      onChange={(e) => setSettings(prev => ({ ...prev, gmail_client_id: e.target.value }))}
                      placeholder="xxxx.apps.googleusercontent.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Secret *</Label>
                    <div className="relative">
                      <Input
                        type={showSecrets.gmail_secret ? 'text' : 'password'}
                        value={settings.gmail_client_secret}
                        onChange={(e) => setSettings(prev => ({ ...prev, gmail_client_secret: e.target.value }))}
                        placeholder="GOCSPX-xxxx"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        onClick={() => toggleSecret('gmail_secret')}
                      >
                        {showSecrets.gmail_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Redirect URI (Add this to Google Console)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={getRedirectUri('gmail')}
                      readOnly
                      className="font-mono text-sm bg-muted"
                    />
                    <Button variant="outline" size="sm" onClick={() => {
                      navigator.clipboard.writeText(getRedirectUri('gmail'));
                      toast.success('Redirect URI copied!');
                    }}>
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => testConnection('gmail')}
                    disabled={testing.gmail || !settings.gmail_client_id || !settings.gmail_client_secret}
                  >
                    {testing.gmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TestTube className="h-4 w-4 mr-2" />}
                    Test Connection
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Microsoft 365 Configuration */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <MicrosoftIcon />
                    </div>
                    <div>
                      <CardTitle>Microsoft 365 / Outlook</CardTitle>
                      <CardDescription>Connect Microsoft 365 and Outlook accounts using Microsoft Graph API</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(settings.microsoft_status)}
                    <Switch
                      checked={settings.microsoft_enabled}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, microsoft_enabled: checked }))}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-700 dark:text-blue-300">Setup Instructions:</p>
                      <ol className="list-decimal ml-4 mt-1 text-blue-600 dark:text-blue-400 space-y-1">
                        <li>Go to <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">Azure App Registrations <ExternalLink className="h-3 w-3" /></a></li>
                        <li>Create a new registration</li>
                        <li>Add Microsoft Graph permissions: Mail.Read, Mail.Send, Mail.ReadWrite</li>
                        <li>Create a client secret</li>
                        <li>Add the redirect URI shown below</li>
                      </ol>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Application (Client) ID *</Label>
                    <Input
                      value={settings.microsoft_client_id}
                      onChange={(e) => setSettings(prev => ({ ...prev, microsoft_client_id: e.target.value }))}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Secret *</Label>
                    <div className="relative">
                      <Input
                        type={showSecrets.microsoft_secret ? 'text' : 'password'}
                        value={settings.microsoft_client_secret}
                        onChange={(e) => setSettings(prev => ({ ...prev, microsoft_client_secret: e.target.value }))}
                        placeholder="xxxxxx~xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        onClick={() => toggleSecret('microsoft_secret')}
                      >
                        {showSecrets.microsoft_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Directory (Tenant) ID *</Label>
                  <Input
                    value={settings.microsoft_tenant_id}
                    onChange={(e) => setSettings(prev => ({ ...prev, microsoft_tenant_id: e.target.value }))}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx or 'common' for multi-tenant"
                  />
                  <p className="text-xs text-muted-foreground">Use &apos;common&apos; to allow any Microsoft account, or specific tenant ID for single organization</p>
                </div>

                <div className="space-y-2">
                  <Label>Redirect URI (Add this to Azure Portal)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={getRedirectUri('microsoft')}
                      readOnly
                      className="font-mono text-sm bg-muted"
                    />
                    <Button variant="outline" size="sm" onClick={() => {
                      navigator.clipboard.writeText(getRedirectUri('microsoft'));
                      toast.success('Redirect URI copied!');
                    }}>
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => testConnection('microsoft')}
                    disabled={testing.microsoft || !settings.microsoft_client_id || !settings.microsoft_client_secret || !settings.microsoft_tenant_id}
                  >
                    {testing.microsoft ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TestTube className="h-4 w-4 mr-2" />}
                    Test Connection
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sync Tab */}
          <TabsContent value="sync" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5" />
                  Sync Settings
                </CardTitle>
                <CardDescription>Configure how emails are synchronized from connected accounts</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Sync Frequency</Label>
                    <Select
                      value={String(settings.sync_frequency_minutes)}
                      onValueChange={(value) => setSettings(prev => ({ ...prev, sync_frequency_minutes: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Every 1 minute</SelectItem>
                        <SelectItem value="5">Every 5 minutes</SelectItem>
                        <SelectItem value="15">Every 15 minutes</SelectItem>
                        <SelectItem value="30">Every 30 minutes</SelectItem>
                        <SelectItem value="60">Every hour</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Max Emails Per Sync</Label>
                    <Select
                      value={String(settings.max_emails_per_sync)}
                      onValueChange={(value) => setSettings(prev => ({ ...prev, max_emails_per_sync: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50 emails</SelectItem>
                        <SelectItem value="100">100 emails</SelectItem>
                        <SelectItem value="200">200 emails</SelectItem>
                        <SelectItem value="500">500 emails</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Link2 className="h-4 w-4" />
                    Auto-Linking
                  </h4>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Auto-link to Contacts</Label>
                      <p className="text-sm text-muted-foreground">Automatically link emails to matching contacts by email address</p>
                    </div>
                    <Switch
                      checked={settings.auto_link_contacts}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_link_contacts: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Auto-link to Jobs</Label>
                      <p className="text-sm text-muted-foreground">Automatically link emails to jobs based on contact associations</p>
                    </div>
                    <Switch
                      checked={settings.auto_link_jobs}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_link_jobs: checked }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Tab */}
          <TabsContent value="ai" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Prime AI Email Settings
                </CardTitle>
                <CardDescription>Configure AI-powered email features and approval workflows</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable AI Email Drafts</Label>
                    <p className="text-sm text-muted-foreground">Allow Prime AI to draft email responses</p>
                  </div>
                  <Switch
                    checked={settings.ai_drafts_enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, ai_drafts_enabled: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Require Human Approval</Label>
                    <p className="text-sm text-muted-foreground">AI-drafted emails must be reviewed before sending (recommended)</p>
                  </div>
                  <Switch
                    checked={settings.ai_draft_review_required}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, ai_draft_review_required: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Auto-categorize Emails</Label>
                    <p className="text-sm text-muted-foreground">Use AI to automatically categorize and tag incoming emails</p>
                  </div>
                  <Switch
                    checked={settings.ai_auto_categorize}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, ai_auto_categorize: checked }))}
                  />
                </div>

                {settings.ai_drafts_enabled && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-700 dark:text-amber-300">Draft-First Policy</p>
                        <p className="text-amber-600 dark:text-amber-400 mt-1">
                          Prime AI follows a &quot;draft-first&quot; policy. All AI-generated emails are saved as drafts 
                          and require explicit approval before being sent. This ensures compliance and maintains 
                          human oversight of all outgoing communications.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  Email Composition
                </CardTitle>
                <CardDescription>Default settings for composing emails</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Default Email Signature</Label>
                  <Textarea
                    value={settings.default_signature}
                    onChange={(e) => setSettings(prev => ({ ...prev, default_signature: e.target.value }))}
                    placeholder="Best regards,\n[Your Name]\n[Your Title]\nRhinoShield Florida"
                    rows={5}
                  />
                  <p className="text-xs text-muted-foreground">This signature will be added to outgoing emails. Users can customize their own signatures.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Notifications
                </CardTitle>
                <CardDescription>Configure email-related notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>New Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">Show notifications for new incoming emails</p>
                  </div>
                  <Switch
                    checked={settings.notify_new_emails}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, notify_new_emails: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Linked Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">Notify when emails are linked to contacts or jobs</p>
                  </div>
                  <Switch
                    checked={settings.notify_linked_emails}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, notify_linked_emails: checked }))}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
